import API_BASE_URL from '../config';

export const loginUser = async (email, password) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/login/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
      credentials: 'include', // Include cookies in the request
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Login failed');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw new Error(error.message);
  }
};

export const fetchCsrfToken = async () => {
  const response = await fetch(`${API_BASE_URL}/get-csrf-token/`, {
    credentials: 'include',
  });
  const data = await response.json();
  console.log("Fetched CSRF Token:", data.csrfToken);
  return data.csrfToken;
};

export const registerUser = async (firstName, lastName, email, password, passwordConfirm, preferredName) => {
  try {
    const csrfToken = await fetchCsrfToken();
    console.log("CSRF Token for Registration:", csrfToken);

    const response = await fetch(`${API_BASE_URL}/register/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken,
      },
      credentials: 'include', // Include cookies in the request
      body: JSON.stringify({
        first_name: firstName,
        last_name: lastName,
        email,
        password,
        password_confirm: passwordConfirm,
        preferred_name: preferredName,
      }),
    });

    console.log("Request Headers:", response.headers);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Registration failed');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Registration Error:", error.message);
    throw new Error(error.message);
  }
};

export const getCampaigns = async () => {
  console.log('Fetching campaigns from:', `${API_BASE_URL}/api/get_campaigns/`);
  try {
    const response = await fetch(`${API_BASE_URL}/api/get_campaigns/`, {
      credentials: 'include',
    });
    console.log('Response status:', response.status);
    if (!response.ok) {
      const errorData = await response.json();
      console.error('Error fetching campaigns:', errorData);
      throw new Error('Failed to fetch campaigns');
    }
    const data = await response.json();
    console.log('Fetched campaigns data:', data);
    return data;
  } catch (error) {
    console.error('Error in getCampaigns:', error.message);
    throw new Error(error.message);
  }
};

export const getCampaignDetails = async (campaignId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/get_campaign_details/${campaignId}/`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Failed to fetch campaign details');
    }
    const data = await response.json();
    return data;
  } catch (error) {
    throw new Error(error.message);
  }
};

export const getSessionDetails = async (sessionId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/get_session_details/${sessionId}/`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Failed to fetch session details');
    }
    const data = await response.json();
    return data;
  } catch (error) {
    throw new Error(error.message);
  }
};

export const updateCampaignTitle = async (campaignId, newTitle) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/update_campaign_title/${campaignId}/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ new_title: newTitle }),
    });

    if (!response.ok) {
      throw new Error('Failed to update campaign title');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw new Error(error.message);
  }
};

export const updateTranscript = async (transcriptId, newText) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/update_transcript/${transcriptId}/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ new_text: newText }),
    });

    if (!response.ok) {
      throw new Error('Failed to update transcript');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw new Error(error.message);
  }
};

export const updateSummary = async (transcriptId, summaryType, newText) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/update_summary/${transcriptId}/${summaryType}/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ new_text: newText }),
    });

    if (!response.ok) {
      throw new Error('Failed to update summary');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw new Error(error.message);
  }
};

export const createCampaign = async (campaignName) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/create_campaign/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ campaign_name: campaignName }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create campaign');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw new Error(error.message);
  }
};

export const deleteCampaign = async (campaignId, password) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/delete_campaign/${campaignId}/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ password }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete campaign');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw new Error(error.message);
  }
};

export const uploadAudio = async (audioFile, campaignId) => {
  try {
    const formData = new FormData();
    formData.append('audio', audioFile);
    formData.append('campaign_id', campaignId);

    const csrfToken = await fetchCsrfToken();

    const response = await fetch(`${API_BASE_URL}/upload/`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'X-CSRFToken': csrfToken,
      },
      body: formData,
    });

    if (!response.ok) {
      if (response.status === 413) {
        throw new Error('File size too large. Please upload a smaller file (max 1GB).');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to upload audio file');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Upload error:', error);
    if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw error;
  }
};

export const getCampaignSummaries = async (campaignId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/get_campaign_summaries/${campaignId}/`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Failed to fetch campaign summaries');
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching campaign summaries:', error);
    throw new Error(error.message);
  }
};

export const updateCampaignSummary = async (campaignId, summaryType, newText) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/update_campaign_summary/${campaignId}/${summaryType}/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ new_text: newText }),
    });

    if (!response.ok) {
      throw new Error('Failed to update campaign summary');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating campaign summary:', error);
    throw new Error(error.message);
  }
};

export const getAccountInfo = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/account-info/`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Failed to fetch account information');
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching account information:', error);
    throw error;
  }
};

export const subscribeEmail = async (email) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/subscribe_email/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Email subscription failed');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw new Error(error.message);
  }
};
